import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { perfumeAPI, transitOrdersAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ArrowLeft,
  Search,
  Check,
  Plus,
  Minus,
  PackageCheck,
} from "lucide-react";

const TransitIn = () => {
  const navigate = useNavigate();
  const [perfumes, setPerfumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [stockItems, setStockItems] = useState({});
  const [processing, setProcessing] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    loadPerfumes();
  }, []);

  const loadPerfumes = async () => {
    try {
      const res = await perfumeAPI.getAll();
      setPerfumes(res.data);
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = (perfumeId, delta) => {
    setStockItems((prev) => {
      const current = prev[perfumeId] || 0;
      const newVal = Math.max(0, current + delta);
      if (newVal === 0) {
        const { [perfumeId]: removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [perfumeId]: newVal };
    });
  };

  const setQuantity = (perfumeId, value) => {
    const numValue = parseFloat(value) || 0;
    if (numValue <= 0) {
      const { [perfumeId]: removed, ...rest } = stockItems;
      setStockItems(rest);
    } else {
      setStockItems({ ...stockItems, [perfumeId]: numValue });
    }
  };

  const confirmStock = async () => {
    const items = Object.entries(stockItems);
    if (items.length === 0) {
      toast.error("Seleziona almeno un profumo");
      return;
    }

    setProcessing(true);
    let successCount = 0;
    let errorCount = 0;

    for (const [perfumeId, liters] of items) {
      try {
        await perfumeAPI.addStock(perfumeId, liters);
        successCount++;
      } catch (error) {
        errorCount++;
        console.error(`Errore per profumo ${perfumeId}:`, error);
      }
    }

    // Save order to history
    if (successCount > 0) {
      try {
        const orderItemsList = items.map(([perfumeId, liters]) => {
          const perfume = perfumes.find((p) => p.id === perfumeId);
          return {
            perfume_id: perfumeId,
            perfume_code: perfume?.code || "",
            perfume_name: perfume?.name || "",
            quantity_liters: liters,
          };
        });
        await transitOrdersAPI.create({
          order_type: "in",
          items: orderItemsList,
        });
      } catch (error) {
        console.error("Error saving order:", error);
      }
    }

    setProcessing(false);

    if (successCount > 0) {
      toast.success(`Stock aggiornato per ${successCount} profumi`);
      setStockItems({});
      loadPerfumes();
    }
    if (errorCount > 0) {
      toast.error(`Errore per ${errorCount} profumi`);
    }
  };

  // Smart search: exact match first, then starts with, then contains
  const filteredPerfumes = perfumes
    .filter(
      (p) =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.code.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      const query = searchQuery.toLowerCase();
      const aCode = a.code.toLowerCase();
      const bCode = b.code.toLowerCase();
      
      // Exact match first
      if (aCode === query && bCode !== query) return -1;
      if (bCode === query && aCode !== query) return 1;
      
      // Then starts with
      const aStarts = aCode.startsWith(query);
      const bStarts = bCode.startsWith(query);
      if (aStarts && !bStarts) return -1;
      if (bStarts && !aStarts) return 1;
      
      // Sort by code length (shorter = more relevant)
      if (aStarts && bStarts) {
        return aCode.length - bCode.length;
      }
      
      return 0;
    });

  const itemCount = Object.keys(stockItems).length;
  const totalLiters = Object.values(stockItems).reduce((sum, val) => sum + val, 0);

  return (
    <div data-testid="transit-in-page" className="animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/transito")}
            className="p-2.5 text-gray-500 hover:text-[#C9A227] bg-white rounded-xl shadow-sm hover:shadow transition-all"
            data-testid="back-to-transit-btn"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <div>
            <h1 className="font-serif text-3xl text-gray-900">Ordine in Entrata</h1>
            <p className="text-gray-500 text-sm mt-1">
              Registra i profumi arrivati dal fornitore
            </p>
          </div>
        </div>
        <Button
          onClick={confirmStock}
          data-testid="confirm-stock-btn"
          disabled={itemCount === 0 || processing}
          className="bg-emerald-500 text-white hover:bg-emerald-600 rounded-xl px-6 h-12 text-sm font-semibold shadow-md hover:shadow-lg disabled:opacity-50 transition-all"
        >
          <PackageCheck className="w-4 h-4 mr-2" strokeWidth={2} />
          {processing ? "Aggiornamento..." : `Conferma (${itemCount})`}
        </Button>
      </div>

      {/* Search */}
      <div className="relative mb-8 max-w-xl">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" strokeWidth={1.5} />
        <Input
          ref={searchRef}
          data-testid="search-transit-in"
          placeholder="Cerca per nome o codice..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-12 bg-white border-gray-200 rounded-xl h-12 focus:border-[#C9A227] text-gray-900 placeholder:text-gray-400 shadow-sm"
        />
      </div>

      {/* Perfume Grid */}
      {loading ? (
        <div className="text-center py-20 text-gray-500">Caricamento...</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredPerfumes.map((perfume) => {
            const quantity = stockItems[perfume.id] || 0;
            const isSelected = quantity > 0;

            return (
              <div
                key={perfume.id}
                className={`perfume-transit-card p-5 ${isSelected ? "selected" : ""}`}
                data-testid={`perfume-in-card-${perfume.code}`}
              >
                <div className="mb-4">
                  <p className="text-[#C9A227] text-xs font-semibold tracking-wider mb-1">{perfume.code}</p>
                  <h3 className="text-gray-900 font-bold text-sm leading-tight line-clamp-2 h-10">
                    {perfume.name}
                  </h3>
                  <p className="text-gray-500 text-xs mt-1 font-medium">
                    Stock attuale: {(perfume.quantity_ml / 1000).toFixed(2)} L
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateQuantity(perfume.id, -1)}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    data-testid={`decrease-in-${perfume.code}`}
                  >
                    <Minus className="w-4 h-4 text-gray-600" strokeWidth={2} />
                  </button>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    value={quantity || ""}
                    onChange={(e) => setQuantity(perfume.id, e.target.value)}
                    placeholder="0"
                    className="w-20 h-10 text-center bg-white border-gray-200 rounded-lg text-gray-900 font-semibold"
                    data-testid={`quantity-in-${perfume.code}`}
                  />
                  <button
                    onClick={() => updateQuantity(perfume.id, 1)}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    data-testid={`increase-in-${perfume.code}`}
                  >
                    <Plus className="w-4 h-4 text-gray-600" strokeWidth={2} />
                  </button>
                  <span className="text-gray-500 text-sm font-medium ml-1">L</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary Bar */}
      {itemCount > 0 && (
        <div className="fixed bottom-6 right-6 left-6 md:left-auto md:w-96">
          <div className="bg-white border-2 border-emerald-200 rounded-2xl p-5 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-gray-500 font-medium">Riepilogo Entrata</span>
              <span className="text-emerald-600 text-sm font-bold">{itemCount} profumi</span>
            </div>
            <div className="max-h-32 overflow-y-auto space-y-2 mb-4">
              {Object.entries(stockItems).map(([id, qty]) => {
                const p = perfumes.find((x) => x.id === id);
                return p ? (
                  <div key={id} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 truncate max-w-[200px] font-medium">{p.name}</span>
                    <span className="text-emerald-600 font-bold">+{qty} L</span>
                  </div>
                ) : null;
              })}
            </div>
            <div className="border-t border-gray-100 pt-3 mb-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm font-medium">Totale</span>
                <span className="text-emerald-600 font-bold text-lg">+{totalLiters.toFixed(1)} L</span>
              </div>
            </div>
            <Button
              onClick={confirmStock}
              disabled={processing}
              className="w-full bg-emerald-500 text-white hover:bg-emerald-600 rounded-xl h-11 text-sm font-semibold shadow-md"
              data-testid="confirm-stock-summary-btn"
            >
              <Check className="w-4 h-4 mr-2" strokeWidth={2} />
              {processing ? "Aggiornamento..." : "Conferma Entrata"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransitIn;
